import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab
} from "@mui/material";
import {
  isPermissionDenied,
  getPermissionDeniedMessage,
} from "@1823-partners/core";
import { getPricingDates } from "../api";
import PricingGrid from "./PricingGrid";
import BrokersGrid from "./BrokersGrid";
import AggregatedPricing from "./AggregatedPricing";
import MonthOverMonthPricingChecks from "./MonthOverMonthPricingChecks";
import PricingFrequency from "./PricingFrequency";

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Dashboard = ({ userId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [error, setError] = useState(null);
  const [livePricingData, setLivePricingData] = useState([]);
  const [liveBrokersData, setLiveBrokersData] = useState([]);
  const [liveDate, setLiveDate] = useState(null);

  useEffect(() => {
    const loadAvailableDates = async () => {
      try {
        setIsLoadingDates(true);
        setError(null);
        const response = await getPricingDates();
        const dates = response.data || [];
        setAvailableDates(dates);

        // Auto-select the most recent date if available
        if (dates.length > 0) {
          setSelectedDate(dates[0]);
        }
      } catch (error) {
        console.error('Error fetching pricing dates:', error);
        if (isPermissionDenied(error)) {
          setError(getPermissionDeniedMessage(error));
        } else {
          setError('Failed to load available dates');
        }
      } finally {
        setIsLoadingDates(false);
      }
    };

    loadAvailableDates();
  }, []);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // PricingGrid reports the rows it shows, the broker rows they were merged
  // with, and the date they were loaded for, so the aggregate never averages
  // one date's rows under another date's heading.
  const handlePricingDataChange = useCallback((pricingData, brokersData, loadedDate) => {
    setLivePricingData(pricingData);
    setLiveBrokersData(brokersData);
    setLiveDate(loadedDate);
  }, []);

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          mb: 4
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Pricing Dashboard
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
          <Tab label="Pricing" {...a11yProps(0)} />
          <Tab label="Brokers" {...a11yProps(1)} />
          <Tab label="M/M Pricing Checks" {...a11yProps(2)} />
          <Tab label="Pricing Frequency" {...a11yProps(3)} />
        </Tabs>
      </Box>

      {/* Pricing Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            mb: 4
          }}
        >
          <Box sx={{ minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel id="date-select-label">Select Pricing Date</InputLabel>
              <Select
                labelId="date-select-label"
                value={selectedDate}
                label="Select Pricing Date"
                onChange={handleDateChange}
                disabled={isLoadingDates || availableDates.length === 0}
              >
                {availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isLoadingDates && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Loading dates...
                </Typography>
              </Box>
            )}

            {error && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Pricing Grid */}
        <PricingGrid
          userId={userId}
          selectedDate={selectedDate}
          onDataChange={handlePricingDataChange}
        />

        {/* Aggregated Pricing */}
        <AggregatedPricing
          userId={userId}
          selectedDate={selectedDate}
          livePricingData={livePricingData}
          liveBrokersData={liveBrokersData}
          liveDate={liveDate}
        />
      </TabPanel>

      {/* Brokers Tab */}
      <TabPanel value={tabValue} index={1}>
        <BrokersGrid userId={userId} />
      </TabPanel>

      {/* Month Over Month Pricing Checks Tab */}
      <TabPanel value={tabValue} index={2}>
        <MonthOverMonthPricingChecks userId={userId} />
      </TabPanel>

      {/* Pricing Frequency Tab */}
      <TabPanel value={tabValue} index={3}>
        <PricingFrequency userId={userId} />
      </TabPanel>
    </Box>
  );
};

export default Dashboard; 